import { db } from '../config/firebase';
import {
    addDoc, collection, doc, getDocs, onSnapshot, orderBy, query, serverTimestamp,
    Timestamp, updateDoc, where,
} from 'firebase/firestore';
import { toIsoDate } from '../utils/dateHelpers';
import { MONEDA_BUCKETS, SNAPSHOT_SALDO_FIELD, DIFERENCIAS_FIELD } from '../constants/monedas';

const COLLECTION = 'saldosSnapshots';

export const SNAPSHOT_TIPO = {
    INICIAL: 'inicial',
    INTRADIARIO: 'intradiario',
};

/*
 * Crea un nuevo snapshot de saldos. No sobreescribe — cada upload del Excel
 * del custodio crea un documento nuevo que queda como histórico.
 * Conciliación contra el snapshot anterior se calcula en Fase 5.
 */
export const crearSnapshotSaldos = async ({ fecha, tipo, comitentes, uploadedBy }) => {
    if (!fecha) throw new Error('fecha es obligatoria');
    if (!Object.values(SNAPSHOT_TIPO).includes(tipo)) {
        throw new Error(`tipo invalido: ${tipo}`);
    }
    if (!Array.isArray(comitentes)) throw new Error('comitentes debe ser array');

    const totalCuentas = comitentes.length;
    const payload = {
        fecha,
        tipo,
        uploadedAt: serverTimestamp(),
        uploadedBy: uploadedBy || null,
        totalCuentas,
        comitentes,
    };
    const ref = await addDoc(collection(db, COLLECTION), payload);
    return ref.id;
};

/*
 * Suscripción a snapshots de una fecha. callback recibe array ordenado por
 * uploadedAt ascendente (el último es el "snapshot activo").
 */
export const subscribeSnapshotsDelDia = (fecha, callback, onError) => {
    const q = query(
        collection(db, COLLECTION),
        where('fecha', '==', fecha),
        orderBy('uploadedAt', 'asc'),
    );
    return onSnapshot(q, (snapshot) => {
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        callback(data);
    }, (error) => {
        console.error('Error suscribiendo a saldosSnapshots:', error);
        if (onError) onError(error);
    });
};

export const getSnapshotsDelDia = async (fecha) => {
    const q = query(
        collection(db, COLLECTION),
        where('fecha', '==', fecha),
        orderBy('uploadedAt', 'asc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getSnapshotActivo = (snapshots) => {
    if (!Array.isArray(snapshots) || snapshots.length === 0) return null;
    return snapshots[snapshots.length - 1];
};

export const tieneSnapshotInicial = (snapshots) =>
    Array.isArray(snapshots) && snapshots.some(s => s.tipo === SNAPSHOT_TIPO.INICIAL);

export const getFechaSnapshotKey = (date = new Date()) => toIsoDate(date);

/*
 * Devuelve el saldo de un comitente en un snapshot dado.
 * Si el comitente no está en el snapshot, devuelve null (no es lo mismo que 0).
 */
export const getSaldoDeComitente = (snapshot, nroComitente, moneda) => {
    if (!snapshot || !Array.isArray(snapshot.comitentes)) return null;
    const found = snapshot.comitentes.find(c => c.nroComitente === nroComitente);
    if (!found) return null;
    const field = SNAPSHOT_SALDO_FIELD[moneda];
    if (!field) return null;
    return Number(found[field]) || 0;
};

/*
 * Persiste el resultado de la conciliación en el snapshot recién creado.
 * Se llama después de calcular las diferencias en cliente. Si no hay diferencias,
 * igualmente queda guardado el flag de "conciliado" con arrays vacíos.
 */
export const guardarResultadoConciliacion = async (snapshotId, diferencias) => {
    if (!snapshotId) throw new Error('snapshotId obligatorio');
    const ref = doc(db, COLLECTION, snapshotId);
    const porMoneda = MONEDA_BUCKETS.reduce((acc, bucket) => ({
        ...acc,
        [DIFERENCIAS_FIELD[bucket]]: Array.isArray(diferencias?.[bucket]) ? diferencias[bucket] : [],
    }), {});
    await updateDoc(ref, {
        conciliacion: {
            ejecutadaAt: serverTimestamp(),
            ...porMoneda,
        },
    });
};

/*
 * Convierte la hora del snapshot (Timestamp Firestore o Date) a "HH:mm" en local.
 */
export const formatSnapshotHora = (uploadedAt) => {
    if (!uploadedAt) return '--:--';
    const date = uploadedAt instanceof Timestamp ? uploadedAt.toDate() : new Date(uploadedAt);
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
};

// Carga automática desde la API de Rosval (Cloud Function cargarSaldosCustodio*).
const CUSTODIO_META_COLLECTION = 'saldosCustodioMeta';
const CARGA_CUSTODIO_FUNCTION_URL =
    'https://us-central1-planilla-operaciones-axon.cloudfunctions.net/cargarSaldosCustodioPublic';

/*
 * Estado de la carga automática del día: { estado: 'cargada' | 'omitida' | 'error', error, ejecutadaAt }.
 * null si todavía no corrió.
 */
export const subscribeSaldosCustodioMeta = (fecha, callback, onError) => onSnapshot(
    doc(db, CUSTODIO_META_COLLECTION, fecha),
    (snap) => callback(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (error) => {
        console.error('saldosService.subscribeSaldosCustodioMeta failed:', { fecha, error });
        if (onError) onError(error);
    },
);

/* Pide al backend traer ahora los saldos del custodio (mismo proceso que la corrida de las 09:30). */
export const solicitarCargaSaldosCustodio = async (user) => {
    try {
        const idToken = await user.getIdToken();
        const response = await fetch(CARGA_CUSTODIO_FUNCTION_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({}),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
        return body;
    } catch (error) {
        console.error('saldosService.solicitarCargaSaldosCustodio failed:', error);
        throw error;
    }
};
